import React, { useMemo, useRef, useState } from 'react';
import { Button, makeStyles, Typography } from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { EditorTable, TableShape, useTableMapWizard } from '../context';
import MapEditorCanvas, { clientToSVG } from '../components/MapEditorCanvas';
import PlacedTable from '../components/PlacedTable';
import SelectionHighlight from '../components/SelectionHighlight';
import TableFigure from '../components/TableFigure';
import TableHistoryControls from '../components/TableHistoryControls';
import TableOverlapNotice from '../components/TableOverlapNotice';
import ZoomControls from '../components/ZoomControls';
import { overlappingTableIds } from '../features';
import { nextTableLabel, TABLE_SHAPE_NAME_KEY } from '../tableEdit';
import {
    clamp,
    clampCenterToArea,
    computeRoom,
    Point,
    SizeMeters,
    tableFootprintMeters,
    tableFootprintPx,
} from '../mapGeometry';

const MAGENTA = theme.palette.primary.main;
const MAGENTA_DARK = theme.palette.primary.dark;
const MAGENTA_LIGHT = 'rgba(153, 86, 126, 0.08)';
const PALETTE_ICON_PX_PER_METER = 18;

interface TableTypeConfig {
    shape: TableShape;
    seats: number;
    labelKey: string;
}

const TABLE_TYPES: TableTypeConfig[] = [
    { shape: 'round', seats: 4, labelKey: 'tableMaps.wizard.roundTable' },
    { shape: 'square', seats: 4, labelKey: 'tableMaps.wizard.squareTable' },
    { shape: 'rectangle', seats: 8, labelKey: 'tableMaps.wizard.rectangleTable' },
];

const useStyles = makeStyles({
    body: {
        display: 'flex',
        alignItems: 'stretch',
    },
    canvasColumn: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12,
        paddingRight: 24,
    },
    paletteHeading: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.palette.text.primary,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
    },
    palette: {
        display: 'flex',
        gap: 8,
    },
    typeCard: {
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
        backgroundColor: 'white',
        cursor: 'grab',
        userSelect: 'none' as const,
        touchAction: 'none' as const,
        '&:hover': {
            borderColor: MAGENTA,
        },
    },
    typeIcon: {
        width: 28,
        flexShrink: 0,
    },
    typeText: {
        flex: 1,
        minWidth: 0,
    },
    typeName: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    typeSize: {
        fontSize: 11,
        color: theme.palette.text.secondary,
    },
    grip: {
        fontSize: 16,
        color: theme.palette.grey['500'],
    },
    infoChip: {
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
        backgroundColor: theme.palette.grey['100'],
        fontSize: 11,
        color: theme.palette.text.secondary,
    },
    infoIcon: {
        fontSize: 16,
    },
    canvasWrapper: {
        width: '100%',
        height: 560,
        borderRadius: 4,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.grey['300']}`,
        position: 'relative' as const,
    },
    canvasOverlay: {
        position: 'absolute' as const,
        left: 16,
        bottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    sidePanel: {
        width: 272,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12,
        paddingLeft: 24,
        borderLeft: `1px solid ${theme.palette.grey['300']}`,
    },
    panelHeading: {
        fontSize: 13,
        fontWeight: 700,
        color: MAGENTA_DARK,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
    },
    listGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1.2fr 1fr 0.7fr',
        alignItems: 'center',
        columnGap: 8,
        width: '100%',
        padding: '7px 4px',
        fontSize: 12,
        textAlign: 'left' as const,
    },
    listHeader: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        color: theme.palette.text.secondary,
        borderBottom: `1px solid ${theme.palette.grey['400']}`,
    },
    listRow: {
        border: 'none',
        borderBottom: `1px solid ${theme.palette.grey['300']}`,
        background: 'none',
        cursor: 'pointer',
        color: theme.palette.text.primary,
        '&:hover': {
            backgroundColor: theme.palette.grey['100'],
        },
    },
    listRowSelected: {
        backgroundColor: MAGENTA_LIGHT,
    },
    listName: {
        fontWeight: 700,
    },
    listSeats: {
        fontWeight: 700,
        color: MAGENTA,
        textAlign: 'right' as const,
    },
    totals: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '14px 12px',
        borderRadius: 4,
        backgroundColor: MAGENTA_LIGHT,
        fontSize: 12,
        fontWeight: 700,
        color: MAGENTA_DARK,
    },
    hintText: {
        fontSize: 11,
        color: theme.palette.text.secondary,
    },
    deleteButton: {
        alignSelf: 'flex-start',
        color: theme.palette.error.dark,
        borderColor: theme.palette.grey['400'],
        textTransform: 'none' as const,
        fontWeight: 600,
    },
});

function sizeText(size: SizeMeters): string {
    return `${size.width} × ${size.height}`;
}

function TableTypeIcon({ shape, size }: { shape: TableShape; size: SizeMeters }) {
    const width = clamp(size.width * PALETTE_ICON_PX_PER_METER, 6, 28);
    const height = clamp(size.height * PALETTE_ICON_PX_PER_METER, 6, 28);
    return (
        <svg width={28} height={28} viewBox="-14 -14 28 28">
            {shape === 'round' ? (
                <circle r={Math.min(width, height) / 2} fill="white" stroke={MAGENTA} />
            ) : (
                <rect
                    x={-width / 2}
                    y={-height / 2}
                    width={width}
                    height={height}
                    fill="white"
                    stroke={MAGENTA}
                />
            )}
        </svg>
    );
}

export default function TablePlacementStep() {
    const classes = useStyles();
    const { draft, dispatch } = useTableMapWizard();
    const room = useMemo(() => computeRoom(draft.size), [draft.size.width, draft.size.height]);
    const [zoom, setZoom] = useState(1.0);
    const svgRef = useRef<SVGSVGElement>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [placingConfig, setPlacingConfig] = useState<TableTypeConfig | null>(null);
    const ghostRef = useRef<SVGGElement>(null);
    const placingLabel = nextTableLabel(draft.tables);
    const selected = draft.tables.find((t) => t.id === selectedId) ?? null;
    const selectedFigure =
        selected &&
        tableFootprintPx(
            selected.shape,
            selected.width,
            selected.height,
            draft.tableDefaults,
            room
        );
    const totalSeats = draft.tables.reduce((sum, t) => sum + t.seats, 0);

    const overlappingIds = useMemo(
        () => overlappingTableIds(draft.tables, draft.features, draft.size, draft.tableDefaults),
        [draft.tables, draft.features, draft.size, draft.tableDefaults]
    );

    function roomPosition(e: React.PointerEvent): Point | null {
        if (!svgRef.current) return null;
        const p = clientToSVG(svgRef.current, e.clientX, e.clientY);
        const isOverRoom =
            p.x >= room.x &&
            p.x <= room.x + room.width &&
            p.y >= room.y &&
            p.y <= room.y + room.height;
        if (!isOverRoom) return null;
        return clampCenterToArea(
            (p.x - room.x) / room.width,
            (p.y - room.y) / room.height,
            draft.size,
            draft.area
        );
    }

    function moveGhost(position: Point | null) {
        const transform = position
            ? `translate(${room.x + position.x * room.width}, ${room.y + position.y * room.height})`
            : 'translate(-9999, -9999)';
        ghostRef.current?.setAttribute('transform', transform);
    }

    function handlePalettePointerDown(config: TableTypeConfig, e: React.PointerEvent) {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setPlacingConfig(config);
        moveGhost(roomPosition(e));
    }

    function handlePalettePointerMove(e: React.PointerEvent) {
        if (placingConfig) moveGhost(roomPosition(e));
    }

    function handlePalettePointerUp(e: React.PointerEvent) {
        const config = placingConfig;
        const position = roomPosition(e);
        setPlacingConfig(null);
        moveGhost(null);
        if (!config || !position) return;
        const size = draft.tableDefaults[config.shape];
        const id = `table-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        dispatch({
            type: 'ADD_TABLE',
            payload: {
                id,
                label: placingLabel,
                shape: config.shape,
                seats: config.seats,
                x: position.x,
                y: position.y,
                width: size.width,
                height: size.height,
                rotation: 0,
            },
        });
        setSelectedId(id);
    }

    function handleMove(id: string, x: number, y: number) {
        dispatch({ type: 'UPDATE_TABLE_POSITION', payload: { id, x, y } });
        setSelectedId(id);
    }

    function handleDelete(table: EditorTable) {
        dispatch({ type: 'REMOVE_TABLE', payload: { id: table.id } });
        setSelectedId(null);
    }

    function renderGhost() {
        if (!placingConfig) return null;
        const { width, height } = tableFootprintPx(
            placingConfig.shape,
            0,
            0,
            draft.tableDefaults,
            room
        );
        return (
            <TableFigure
                width={width}
                height={height}
                seats={placingConfig.seats}
                shape={placingConfig.shape}
                label={placingLabel}
                isActive={false}
            />
        );
    }

    return (
        <div className={classes.body}>
            <div className={classes.canvasColumn}>
                <Typography className={classes.paletteHeading}>
                    {i18n.t('tableMaps.wizard.tables.typesHeading')}
                </Typography>
                <div className={classes.palette}>
                    {TABLE_TYPES.map((config) => {
                        const size = draft.tableDefaults[config.shape];
                        return (
                            <div
                                key={config.shape}
                                className={classes.typeCard}
                                onPointerDown={(e) => handlePalettePointerDown(config, e)}
                                onPointerMove={handlePalettePointerMove}
                                onPointerUp={handlePalettePointerUp}
                            >
                                <span className={classes.typeIcon}>
                                    <TableTypeIcon shape={config.shape} size={size} />
                                </span>
                                <span className={classes.typeText}>
                                    <Typography className={classes.typeName}>
                                        {i18n.t(config.labelKey)}
                                    </Typography>
                                    <Typography className={classes.typeSize}>
                                        {`${sizeText(size)} m`}
                                    </Typography>
                                </span>
                                <DragIndicatorIcon className={classes.grip} />
                            </div>
                        );
                    })}
                    <div className={classes.infoChip}>
                        <InfoOutlinedIcon className={classes.infoIcon} />
                        {i18n.t('tableMaps.wizard.tables.overlapsWarned')}
                    </div>
                </div>

                <div
                    className={classes.canvasWrapper}
                    style={{ cursor: placingConfig ? 'crosshair' : undefined }}
                >
                    <MapEditorCanvas
                        gridEnabled={draft.gridEnabled}
                        zoom={zoom}
                        size={draft.size}
                        area={draft.area}
                        openings={draft.entrances}
                        features={draft.features}
                        isFeaturesDimmed
                        svgRef={svgRef}
                        showMeasureGuides
                    >
                        <rect
                            x={room.x}
                            y={room.y}
                            width={room.width}
                            height={room.height}
                            fill="transparent"
                            onPointerDown={() => setSelectedId(null)}
                        />
                        {draft.tables.map((table) => (
                            <PlacedTable
                                key={table.id}
                                table={table}
                                room={room}
                                svgRef={svgRef}
                                isOverlapping={overlappingIds.has(table.id)}
                                isLocked={table.isPositionLocked}
                                onSelect={(id) =>
                                    setSelectedId((prev) => (prev === id ? null : id))
                                }
                                onMove={handleMove}
                            >
                                {table.id === selectedId && selectedFigure && (
                                    <SelectionHighlight
                                        figW={selectedFigure.width}
                                        figH={selectedFigure.height}
                                    />
                                )}
                            </PlacedTable>
                        ))}
                        <g
                            ref={ghostRef}
                            transform="translate(-9999, -9999)"
                            opacity={0.7}
                            pointerEvents="none"
                        >
                            {renderGhost()}
                        </g>
                    </MapEditorCanvas>
                    <div className={classes.canvasOverlay}>
                        <ZoomControls
                            zoom={zoom}
                            onZoomChange={setZoom}
                            pxPerMeter={room.pxPerMeter}
                        />
                        <TableHistoryControls />
                    </div>
                </div>
                <TableOverlapNotice tables={draft.tables} overlappingIds={overlappingIds} />
            </div>

            <div className={classes.sidePanel}>
                <Typography className={classes.panelHeading}>
                    {i18n.t('tableMaps.wizard.tables.placedTables')}
                </Typography>
                <div>
                    <div className={`${classes.listGrid} ${classes.listHeader}`}>
                        <span>{i18n.t('tableMaps.wizard.tables.columnName')}</span>
                        <span>{i18n.t('tableMaps.wizard.tables.columnShape')}</span>
                        <span>{i18n.t('tableMaps.wizard.tables.columnSize')}</span>
                        <span className={classes.listSeats} style={{ color: 'inherit' }}>
                            {i18n.t('tableMaps.wizard.tables.columnSeats')}
                        </span>
                    </div>
                    {draft.tables.map((table) => (
                        <button
                            key={table.id}
                            type="button"
                            className={`${classes.listGrid} ${classes.listRow} ${
                                table.id === selectedId ? classes.listRowSelected : ''
                            }`}
                            onClick={() => setSelectedId(table.id)}
                        >
                            <span className={classes.listName}>{table.label}</span>
                            <span>{i18n.t(TABLE_SHAPE_NAME_KEY[table.shape])}</span>
                            <span>
                                {sizeText(
                                    tableFootprintMeters(
                                        table.shape,
                                        table.width,
                                        table.height,
                                        draft.tableDefaults
                                    )
                                )}
                            </span>
                            <span className={classes.listSeats}>{table.seats}</span>
                        </button>
                    ))}
                </div>
                {draft.tables.length === 0 && (
                    <Typography className={classes.hintText}>
                        {i18n.t('tableMaps.wizard.tables.noTables')}
                    </Typography>
                )}
                <div className={classes.totals}>
                    <span>
                        {draft.tables.length === 1
                            ? i18n.t('tableMaps.wizard.tables.tableCountOne')
                            : i18n.t('tableMaps.wizard.tables.tableCount', {
                                  n: draft.tables.length,
                              })}
                    </span>
                    <span>
                        {totalSeats === 1
                            ? i18n.t('tableMaps.wizard.tables.seatCountOne')
                            : i18n.t('tableMaps.wizard.tables.seatCount', { n: totalSeats })}
                    </span>
                </div>
                {selected && (
                    <Button
                        variant="outlined"
                        className={classes.deleteButton}
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => handleDelete(selected)}
                    >
                        {`${i18n.t('tableMaps.wizard.delete')} ${selected.label}`}
                    </Button>
                )}
                <Typography className={classes.hintText}>
                    {i18n.t('tableMaps.wizard.tables.featuresDimmed')}
                </Typography>
            </div>
        </div>
    );
}
